<script setup>
import { computed, ref } from 'vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  maxHeight: {
    type: String,
    default: '130px',
  },
  charLimit: {
    type: Number,
    default: 300,
  },
  showToggle: {
    type: Boolean,
    default: true,
  },
  expandText: {
    type: String,
    default: 'Show more',
  },
  collapseText: {
    type: String,
    default: 'Show less',
  },
});

const isExpanded = ref(false);
const isLongContent = computed(() => props.content.length > props.charLimit);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div class="relative flex flex-col">
    <div
      class="transition-[max-height] duration-300 ease-in-out overflow-hidden"
      :class="isExpanded ? 'max-h-none' : ''"
      :style="{ maxHeight: isExpanded ? 'none' : maxHeight }"
    >
      <slot v-if="$slots.default" />
      <WootMessageEditor
        v-else
        :model-value="content"
        :enabled-menu-options="[' ']"
        disabled
        class="expandable-message-editor py-5 px-4"
      />
    </div>

    <div
      v-if="showToggle && isLongContent && !isExpanded"
      class="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-n-alpha-2 via-n-alpha-2/60 to-transparent pointer-events-none"
      aria-hidden="true"
    />

    <button
      v-if="showToggle && isLongContent"
      class="flex items-center justify-center gap-2 w-full py-2 border-t border-n-strong hover:bg-n-strong/30 transition-colors group focus:outline-none mt-2"
      @click="toggleExpand"
    >
      <span
        class="text-xs font-medium text-n-slate-11 group-hover:text-n-slate-12 transition-colors"
      >
        {{ isExpanded ? collapseText : expandText }}
      </span>
      <i
        :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="w-4 h-4 text-n-slate-11 group-hover:text-n-slate-12 transition-colors"
      />
    </button>
  </div>
</template>

<style lang="scss">
.expandable-message-editor {
  .ProseMirror-menubar-wrapper {
    .ProseMirror-menubar {
      display: none;
    }
  }

  .ProseMirror-woot-style {
    max-height: none;
    overflow: visible;
  }

  .ProseMirror {
    min-height: 0 !important;
    padding: 0 !important;
    background: none !important;

    p {
      margin: 0 !important;
      line-height: 1.6;
    }
  }
}
</style>
